import { Link } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { motion } from "framer-motion";
import { Quote, ArrowRight, Award, Flag, Shield } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const milestones = [
  { year: "2018", title: "The Spark", text: "After witnessing his community being terrorised by a group of illegals, David decided he would no longer stand on the sidelines. He held a community meeting and pitched his idea of a political organization and citizen defense network to protect not only his neighborhood but his entire country from the radical islam/communists/DSA/Socialists." },
  { year: "2019", title: "First Chapters", text: "The model spread. Within two years, local chapters formed across multiple states under the Patriot mission to protect our Country." },
  { year: "2023", title: "National Reach", text: "Patriot Power USA formalized as a national organization with coordinated training, standards, and rapid-response capabilities." },
  { year: "Today", title: "The Mission Continues", text: "Thousands of members strong, the movement keeps growing — one prepared citizen, one protected community at a time." },
];

export default function Founder() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="bg-[#0A1128] text-white pt-40 pb-24 grain stars tactical-grid relative overflow-hidden">
        <div className="absolute top-1/4 -left-40 w-[560px] h-[560px] glow-red pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center relative z-10">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="kicker-pill mb-5">Our Founder</span>
            <h1 className="font-display text-5xl sm:text-7xl uppercase mt-4 leading-[0.92]">
              D.J. <span className="text-[#D90429]">Colace</span>
            </h1>
            <p className="text-white/60 uppercase tracking-[0.2em] text-sm mt-3">Founder &amp; President</p>
            <p className="text-white/80 text-lg mt-6 leading-relaxed">
              A lifelong patriot, community organizer, and advocate for citizen preparedness, D.J. Colace founded
              Patriot Power USA with a conviction that ordinary Americans, properly trained and united,
              are the strongest line of defense a free nation can have.
            </p>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1} className="relative" data-testid="founder-monogram">
            <div className="w-full h-[520px] bg-[#0d1738] border border-white/10 flex flex-col items-center justify-center grain">
              <Shield className="w-16 h-16 text-[#D90429] mb-6" />
              <div className="font-display text-8xl uppercase text-white tracking-tight leading-none">D.J.C.</div>
              <div className="w-16 h-[3px] bg-[#D90429] my-6" />
              <p className="text-white/50 uppercase tracking-[0.3em] text-xs">Founder &amp; President</p>
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#D90429] text-white p-6 hidden sm:block">
              <Award className="w-8 h-8" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-24 bg-muted">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Quote className="w-12 h-12 text-[#D90429] mx-auto mb-6" />
          <p className="font-display text-3xl sm:text-4xl uppercase leading-tight text-[#0A1128]">
            "Freedom is a responsibility, not a spectator sport. When we prepare together, we protect together."
          </p>
          <p className="text-slate-500 uppercase tracking-[0.2em] text-sm mt-8">— D.J. Colace, Founder</p>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-14">
            <Flag className="w-8 h-8 text-[#D90429]" />
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">The Journey</h2>
          </div>
          <div className="space-y-0">
            {milestones.map((m, i) => (
              <motion.div key={m.year} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid="milestone" className="grid sm:grid-cols-[160px_1fr] gap-6 border-l-2 border-[#D90429] pl-8 pb-12 relative">
                <div className="absolute -left-[9px] top-1 w-4 h-4 bg-[#D90429]" />
                <div className="font-display text-3xl text-[#0A1128]">{m.year}</div>
                <div>
                  <h3 className="font-display text-2xl uppercase mb-2">{m.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{m.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-20 grain stars relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <Shield className="w-10 h-10 text-[#D90429] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Moving The Mission Forward</h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto">D.J. Colace started with a handful of neighbors. Today the movement needs you.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/join" data-testid="founder-join-button" className="bg-white text-[#0A1128] px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#F4A261] transition-colors duration-200 active:scale-95">Join Our Newsletter</Link>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="founder-donate-button" className="inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95">Support The Mission! <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
